import type { HTMLAttributes } from 'react';
import { cx } from '../lib/cx';

export interface LogoProps extends HTMLAttributes<HTMLSpanElement> {
  /** The bars alone, without the wordmark — for tight bars and narrow screens. */
  markOnly?: boolean;
  /** The rounded app-icon tile (green square, white bars) — favicons, avatars,
   *  anywhere the logo needs its own container. */
  tile?: boolean;
  size?: 'sm' | 'md' | 'lg';
  /** Render on a dark/inverse surface: the wordmark and bars go light. */
  inverse?: boolean;
}

/**
 * TopCall24 brand lock-up — the rising-bars mark plus the wordmark.
 *
 * Geometry is copied from `design/theme/brand/topcall24-logo-*.svg`: five bars,
 * 5 wide on a 9px pitch, all centred on the same axis, with the tallest in the
 * middle carrying the deeper accent. Drawn inline rather than loaded as an
 * `<img>` so one component covers light, dark and inverse surfaces (the shipped
 * SVGs are two fixed-colour files) and stays sharp at any size with no request.
 */
export function Logo({ markOnly = false, tile = false, size = 'md', inverse = false, className, ...rest }: LogoProps) {
  return (
    <span
      className={cx('tc-logo', size !== 'md' && `tc-logo--${size}`, inverse && 'tc-logo--inverse', className)}
      role="img"
      aria-label="TopCall24"
      {...rest}
    >
      {tile ? <LogoTile /> : <LogoBars />}
      {!markOnly && !tile && (
        <span className="tc-logo__word" aria-hidden>
          TopCall<span>24</span>
        </span>
      )}
    </span>
  );
}

/** The five bars on their own, on a transparent ground. */
function LogoBars() {
  return (
    <svg className="tc-logo__mark" viewBox="0 0 41 44" fill="none" aria-hidden focusable="false">
      <rect x="0" y="15" width="5" height="14" rx="2.5" />
      <rect x="9" y="7" width="5" height="30" rx="2.5" />
      <rect className="tc-logo__mark-accent" x="18" y="0" width="5" height="44" rx="2.5" />
      <rect x="27" y="10" width="5" height="24" rx="2.5" />
      <rect x="36" y="16" width="5" height="12" rx="2.5" />
    </svg>
  );
}

/** The app-icon form: white bars on a rounded brand-green tile. */
function LogoTile() {
  return (
    <svg className="tc-logo__tile" viewBox="0 0 64 64" aria-hidden focusable="false">
      <rect width="64" height="64" rx="14" fill="var(--tc-primary)" />
      <g fill="#FFFFFF">
        <rect x="11.5" y="26" width="5" height="12" rx="2.5" />
        <rect x="20.5" y="19" width="5" height="26" rx="2.5" opacity="0.75" />
        <rect x="29.5" y="13" width="5" height="38" rx="2.5" />
        <rect x="38.5" y="22" width="5" height="20" rx="2.5" opacity="0.75" />
        <rect x="47.5" y="27" width="5" height="10" rx="2.5" />
      </g>
    </svg>
  );
}

/** Google's four-colour "G" — the only brand asset we render verbatim, so it
 *  keeps its own palette rather than currentColor (Google brand guidelines). */
export function GoogleMark({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden focusable="false">
      <path fill="#4285F4" d="M45.1 24.5c0-1.6-.1-3.2-.4-4.7H24v8.9h11.8a10 10 0 0 1-4.4 6.6v5.5h7.1c4.2-3.8 6.6-9.5 6.6-16.3z" />
      <path fill="#34A853" d="M24 46c6 0 11-2 14.5-5.2l-7.1-5.5c-2 1.3-4.5 2.1-7.4 2.1-5.7 0-10.6-3.9-12.3-9.1H4.3v5.7A22 22 0 0 0 24 46z" />
      <path fill="#FBBC05" d="M11.7 28.3a13.2 13.2 0 0 1 0-8.6v-5.7H4.3a22 22 0 0 0 0 20l7.4-5.7z" />
      <path fill="#EA4335" d="M24 9.5c3.2 0 6.1 1.1 8.4 3.3l6.3-6.3C34.9 2.9 30 1 24 1A22 22 0 0 0 4.3 14l7.4 5.7c1.7-5.2 6.6-9.2 12.3-9.2z" />
    </svg>
  );
}
